'use strict';

import { CoreRepository } from '../CoreRepository';
import type { CommissionStatementMapping } from '../../Entities/CommissionStatement/CommissionStatementMapping';
import { CommissionStatementMappingCompanyCollection } from '../../Collections/CommissionStatement/CommissionStatementMappingCompanyCollection';
import type { GlobCompanies } from '../../Entities/Glob/GlobCompanies';
import { CommissionStatementMappingCompany } from '../../Entities/CommissionStatement/CommissionStatementMappingCompany';

/**
 * Complex type to be used in the SDK
 */
export interface CommissionStatementMappingCompanySchema {
  referencedValues?: Array<string>;
  company?: GlobCompanies;
  commissionStatementMapping?: CommissionStatementMapping;
}

/**
 * Simple type for moving data between the SDK and the API
 */
export interface CommissionStatementMappingCompanyPayload {
  referencedValues?: Array<string>;
  company?: number;
  commissionStatementMapping?: number;
}

/**
 * Format when returning a list of CommissionStatementMapping entities from the API
 */
export interface CommissionStatementMappingCompanyCollectionPayload {
  limit?: number;
  total?: number;
  offset?: number;
  data?: Array<CommissionStatementMappingCompanyPayload>;
}

/**
 * Repository for CRUD operations within the context of a CommissionStatementMapping type
 *
 * @author Matt Lowden <matt.lowden@medicareprocrm.com>
 */
export class CommissionStatementMappingCompanyRepository extends CoreRepository {
  /**
   * Takes data supplied in the CommissionStatementMappingCompanySchema format and converts it to a simplified version
   * for use with the API
   *
   * @param {CommissionStatementMappingCompanySchema} data Data to be converted to a simplified format for usage with the API
   * @returns {CommissionStatementMappingCompanyPayload} Simplified data structure for use with our API
   */
  protected processData(
    data: Array<CommissionStatementMappingCompanySchema>
  ): Array<CommissionStatementMappingCompanyPayload> {
    const payload: Array<CommissionStatementMappingCompanyPayload> = [];

    for (const dataSingle of data) {
      const payloadSingle: CommissionStatementMappingCompanyPayload = {};

      if (dataSingle.hasOwnProperty('referencedValues')) {
        payloadSingle.referencedValues = dataSingle.referencedValues;
      }

      if (dataSingle.company) {
        payloadSingle.company = dataSingle.company.getCompanyId();
      }

      if (dataSingle.commissionStatementMapping) {
        payloadSingle.commissionStatementMapping = dataSingle.commissionStatementMapping.getId();
      }

      payload.push(payloadSingle);
    }

    return payload;
  }

  /**
   * Update a single CommissionStatementMappingCompany
   *
   * @param {number} userId ID of User whose statement is being requested
   * @param {number} commissionStatementMappingId ID of calendar statement being requested
   * @param {CommissionStatementMappingCompanySchema} data Data to update
   * @returns {Promise<CommissionStatementMappingCompany>} Promise containing requested CommissionStatementMapping
   * @throws {Exception} If no matching statement exists, the user isn't authenticated or suppled CommissionStatementMappingCompanySchema is invalid
   */
  public updateCommissionStatementMappingCompanies(
    userId: number,
    commissionStatementMappingId: number,
    data: Array<CommissionStatementMappingCompanySchema>
  ): Promise<CommissionStatementMappingCompany> {
    const payload = this.processData(data);

    return this.webAPI
      .updateCommissionStatementMappingCompanies(userId, commissionStatementMappingId, payload)
      .then((response) => {
        const item = new CommissionStatementMappingCompany();
        item.hydrate(response);

        return item;
      });
  }
}
